import { useNavigate } from 'react-router-dom';
import {
  UserCircle2,
  ListChecks,
  Waypoints,
  Target,
  Ban,
  FileClock,
  Flag,
  Newspaper,
  AlertTriangle,
  CheckCircle2,
  CalendarPlus,
} from 'lucide-react';
import {
  Badge,
  Button,
  EmptyState,
  PageHeader,
  Spinner,
  type BadgeTone,
} from '../../ui';
import { HomePanel, LinkRow } from '../common/HomePanels';
import { useMyVector } from '../../features/home/useHome';
import type { GoalStatus } from '../../features/goals/types';
import type { TaskStatus } from '../../features/tasks/types';
import type { MilestoneItem } from '../../features/home/homeModel';

const GOAL_STATUS_META: Record<GoalStatus, { label: string; tone: BadgeTone }> = {
  NOT_STARTED: { label: 'Not started', tone: 'neutral' },
  ON_TRACK: { label: 'On track', tone: 'success' },
  AT_RISK: { label: 'At risk', tone: 'warning' },
  OFF_TRACK: { label: 'Off track', tone: 'danger' },
  COMPLETED: { label: 'Completed', tone: 'info' },
};

const TASK_STATUS_META: Record<TaskStatus, { label: string; tone: BadgeTone }> = {
  BACKLOG: { label: 'Backlog', tone: 'neutral' },
  TODO: { label: 'To do', tone: 'neutral' },
  IN_PROGRESS: { label: 'In progress', tone: 'info' },
  BLOCKED: { label: 'Blocked', tone: 'danger' },
  DONE: { label: 'Done', tone: 'success' },
};

/** Human "due in / overdue" caption for a milestone. */
function milestoneWhen(m: MilestoneItem): string {
  if (m.overdue) {
    const d = Math.abs(m.daysUntil);
    return `Overdue by ${d} day${d === 1 ? '' : 's'}`;
  }
  if (m.daysUntil === 0) return 'Due today';
  return `Due in ${m.daysUntil} day${m.daysUntil === 1 ? '' : 's'}`;
}

/**
 * My Vector — the personal home (section 25). A lightweight, single-user view
 * of the current user's own context: active stories, workstreams, goals they
 * contribute to, open blockers, docs awaiting their review, upcoming
 * milestones, whether today's daily update is in, and recent changes in things
 * they follow. Organization + awareness ONLY — nothing here measures or ranks
 * the person.
 */
export function MyVectorContent() {
  const navigate = useNavigate();
  const { model, milestones, alias, loading, error } = useMyVector();

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Spinner label="Loading your Vector" />
      </div>
    );
  }
  if (error || !model) {
    return (
      <EmptyState
        icon={<AlertTriangle className="h-6 w-6" />}
        title="Couldn't load your view"
        description={error ?? 'No context available yet.'}
      />
    );
  }

  const dailyBadge = model.dailyUpdateSubmittedToday ? (
    <Badge tone="success" dot>
      <CheckCircle2 className="h-3 w-3" aria-hidden="true" /> Daily update posted
    </Badge>
  ) : (
    <Button
      variant="secondary"
      size="sm"
      leftIcon={<CalendarPlus className="h-4 w-4" />}
      onClick={() => navigate('/daily-updates')}
    >
      Post today's update
    </Button>
  );

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-zinc-500">
          Your engineering context, organized from what the team has recorded
          {alias ? ` · ${alias}` : ''}. Awareness only — nothing here is a
          performance metric.
        </p>
        {dailyBadge}
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <HomePanel
          title="My active stories"
          icon={ListChecks}
          count={model.activeStories.length}
          empty={model.activeStories.length === 0}
          emptyLabel="No active stories assigned to you."
        >
          {model.activeStories.map((t) => (
            <LinkRow
              key={t.id}
              primary={t.title}
              onClick={() => navigate('/tasks')}
              trailing={
                <Badge tone={TASK_STATUS_META[t.status].tone}>
                  {TASK_STATUS_META[t.status].label}
                </Badge>
              }
            />
          ))}
        </HomePanel>

        <HomePanel
          title="My workstreams"
          icon={Waypoints}
          count={model.workstreams.length}
          empty={model.workstreams.length === 0}
          emptyLabel="You don't own or follow any workstreams yet."
        >
          {model.workstreams.map((w) => (
            <LinkRow
              key={w.id}
              primary={w.title}
              secondary={w.engineer}
              onClick={() => navigate(`/workstreams/${encodeURIComponent(w.id)}`)}
              trailing={<Badge tone="info">{w.currentStage}</Badge>}
            />
          ))}
        </HomePanel>

        <HomePanel
          title="Goals I contribute to"
          icon={Target}
          count={model.goals.length}
          empty={model.goals.length === 0}
          emptyLabel="You're not linked to any goals yet."
        >
          {model.goals.map((g) => (
            <LinkRow
              key={g.id}
              primary={g.title}
              secondary={g.goalFramework}
              onClick={() => navigate(`/goals/${encodeURIComponent(g.id)}`)}
              trailing={
                <Badge tone={GOAL_STATUS_META[g.status].tone}>
                  {GOAL_STATUS_META[g.status].label}
                </Badge>
              }
            />
          ))}
        </HomePanel>

        <HomePanel
          title="My open blockers"
          icon={Ban}
          count={model.openBlockers.length}
          empty={model.openBlockers.length === 0}
          emptyLabel="Nothing is blocking you right now."
        >
          {model.openBlockers.map((b) => (
            <LinkRow
              key={b.id}
              primary={b.summary}
              secondary={`Owner: ${b.dependencyOwner}`}
              onClick={() => navigate('/blockers')}
              trailing={
                <Badge tone={b.internalExternal === 'EXTERNAL' ? 'warning' : 'neutral'}>
                  {b.internalExternal === 'EXTERNAL' ? 'External' : 'Internal'}
                </Badge>
              }
            />
          ))}
        </HomePanel>

        <HomePanel
          title="Awaiting my review"
          icon={FileClock}
          count={model.docsAwaitingReview.length}
          empty={model.docsAwaitingReview.length === 0}
          emptyLabel="No documents are waiting on your review."
        >
          {model.docsAwaitingReview.map((d) => (
            <LinkRow
              key={d.id}
              primary={d.title}
              secondary={d.docType}
              onClick={() => navigate(`/documents/${encodeURIComponent(d.id)}`)}
              trailing={<Badge tone="warning">In review</Badge>}
            />
          ))}
        </HomePanel>

        <HomePanel
          title="Upcoming milestones"
          icon={Flag}
          count={milestones.length}
          empty={milestones.length === 0}
          emptyLabel="No upcoming milestones on your goals."
        >
          {milestones.map((m) => (
            <LinkRow
              key={`${m.goalId}:${m.label}`}
              primary={m.label}
              secondary={m.goalTitle}
              onClick={() => navigate(`/goals/${encodeURIComponent(m.goalId)}`)}
              trailing={
                <Badge tone={m.overdue ? 'danger' : 'neutral'}>
                  {milestoneWhen(m)}
                </Badge>
              }
            />
          ))}
        </HomePanel>

        <HomePanel
          title="Recent changes I follow"
          icon={Newspaper}
          count={model.recentFollowedChanges.length}
          empty={model.recentFollowedChanges.length === 0}
          emptyLabel="No recent changes in the work you follow."
          className="lg:col-span-2"
        >
          {model.recentFollowedChanges.map((c) => (
            <LinkRow
              key={c.id}
              primary={c.title}
              secondary={c.why || c.changed}
              onClick={() => navigate('/changes')}
              trailing={<span className="text-xs text-zinc-500">{c.engineer}</span>}
            />
          ))}
        </HomePanel>
      </div>
    </div>
  );
}

/**
 * My Vector — the personal home (legacy standalone route). The unified Home
 * page now hosts this as its "Me" tab; this wrapper renders the standalone
 * page title + {@link MyVectorContent}.
 */
export function MyVectorPage() {
  return (
    <div>
      <PageHeader
        icon={<UserCircle2 className="h-5 w-5" />}
        title="My Vector"
        description="Your engineering context, organized from what the team has recorded. Awareness only — nothing here is a performance metric."
      />
      <MyVectorContent />
    </div>
  );
}

export default MyVectorPage;
